/**
 * Phase 17 — admin-offer countdown helpers.
 *
 * Pure functions shared by the web `OfferCountdown` component and the
 * React Native `OfferCountdown` parity component. Per `research.md
 * R-003`, countdowns are computed client-side from
 * `offer.expires_at` — no server endpoint is needed. The host
 * component re-renders every 60s via `setInterval`.
 */

/** Milliseconds remaining until `expiresAtIso`. Negative if already past. */
export function remainingMs(expiresAtIso: string, now: Date = new Date()): number {
  const expires = new Date(expiresAtIso).getTime();
  return expires - now.getTime();
}

export function isExpired(expiresAtIso: string, now: Date = new Date()): boolean {
  return remainingMs(expiresAtIso, now) <= 0;
}

/**
 * Render `remainingMs` as a human countdown:
 *   ">24h"  → "2d 4h"
 *   ">1h"   → "5h 12m"
 *   "<1h"   → "42m"
 *   "<60s"  → "expiring soon"
 *   "≤0"    → "expired"
 *
 * Resolution caps out at minutes because the surface re-renders every
 * 60s; sub-minute precision would just flicker.
 */
export function formatCountdown(ms: number): string {
  if (ms <= 0) return "expired";
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return "expiring soon";
  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const totalHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes - totalHours * 60;
  if (totalHours < 24) return `${totalHours}h ${minutes}m`;
  const days = Math.floor(totalHours / 24);
  const hours = totalHours - days * 24;
  return `${days}d ${hours}h`;
}
